// The glazing card (docs/claude/glazings.md): shown under a glazing's door and
// under each of its panes — what the room makes of it (material, layout, hinges,
// corner), the options stored on its door, its product code with the link to
// the PT10 configurator (cut lists, drawings), its weights and net passage, and
// what is out of the spec. A single door (PT9) has its own, shorter card.

import type {
  GlazingOptions,
  ProductInstance,
} from '../../shared/types';
import {
  configuratorUrl,
  type GlazingWarning,
  glazingWeights,
  hasTreatedFace,
  netPassage,
  productCode,
} from '../glazing';
import {
  getUiLang,
  tr,
} from '../i18n';
import type { RoomGlazing } from '../roomGlazing';
import {
  SINGLE_BAND,
  singleDoorSolids,
} from '../singleDoor';
import {
  invalidate,
  pushHistory,
  renderProperties,
} from './core';
import {
  checkboxRow,
  infoRow,
  noteRow,
  sectionTitle,
  selectRow,
} from './panelRows';

const LAYOUT: Record<RoomGlazing['input']['layout'], string> = {
  centro: 'Door between fixed panes',
  destra: 'Door on the right',
  sinistra: 'Door on the left',
};

/** Append the glazing's card to a door's or a pane's panel. */
export function appendGlazingCard(body: HTMLElement, inst: ProductInstance, g: RoomGlazing): void {
  if (g.system === 'pt9') { appendSingleDoorCard(body, inst, g); return; }
  const card = document.createElement('div');
  card.className = 'glazing-card';
  const input = g.input, plan = g.plan;
  card.appendChild(sectionTitle('Glazing'));
  card.appendChild(infoRow('Material', tr(input.mat === 'aluminium' ? 'Aluminium' : 'Wood')));
  card.appendChild(infoRow('Layout', tr(LAYOUT[input.layout])));
  card.appendChild(infoRow('Hinges', tr(input.hinge === 'sx' ? 'Left (seen from outside)' : 'Right (seen from outside)')));
  if (plan.corner) card.appendChild(infoRow('Corner glass', `${tr(input.corner?.side === 'L' ? 'Left' : 'Right')} · ${Math.round(plan.corner.D)} mm`));
  card.appendChild(infoRow('Wall opening', `${Math.round(input.W)} × ${Math.round(input.H)} mm`));

  const set = optionSetter(inst, g);
  card.appendChild(selectRow('Configuration', [['1', '1 · Exposed profile'], ['4', '4 · With decorative trim']], String(input.config), (v) => set({ config: v === '4' ? 4 : 1 })));
  appendDoorOptions(card, g, set);
  card.appendChild(checkboxRow('Threshold', !!input.threshold, (v) => set({ threshold: v })));

  const code = productCode(plan);
  const codeRow = infoRow('Product code', code);
  codeRow.querySelector('.prop-value')?.classList.add('glazing-code');
  card.appendChild(codeRow);
  const link = document.createElement('a');
  link.className = 'link-btn glazing-link';
  link.href = configuratorUrl(code, getUiLang());
  link.target = '_blank';
  link.rel = 'noopener';
  link.textContent = tr('Cut lists and drawings in the PT10 configurator');
  card.appendChild(link);

  const kg = glazingWeights(plan);
  card.appendChild(infoRow('Door leaf + handle', `${kg.leaf.toFixed(1)} kg`));
  card.appendChild(infoRow('Total weight', `${kg.total.toFixed(1)} kg`));
  const pass = netPassage(plan);
  card.appendChild(infoRow('Net passage', `${Math.floor(pass.w + 0.05)} × ${Math.floor(pass.h + 0.05)} mm`));
  for (const w of plan.warnings) card.appendChild(noteRow(warningText(w), 'warn', true));
  body.appendChild(card);
}

// Each option is stored on the door; a change is one undo step.
function optionSetter(inst: ProductInstance, g: RoomGlazing): (patch: Partial<GlazingOptions>) => void {
  return (patch) => {
    const door = (inst.doors ?? []).find((d) => d.id === g.door.id);
    if (!door) return;
    pushHistory();
    door.glazing = { ...door.glazing, ...patch };
    renderProperties();
    invalidate();
  };
}

/** The finish, the glass and its hinges, and the handle: PT10's and PT9's alike. */
function appendDoorOptions(card: HTMLElement, g: RoomGlazing, set: (patch: Partial<GlazingOptions>) => void): void {
  const input = g.input;
  card.appendChild(selectRow('Profile and hardware finish', [['02', '02 Natural aluminium oxidized'], ['05', '05 Matte black oxidized'], ['17', '17 Gold matte coated']], input.finish, (v) => set({ finish: v as GlazingOptions['finish'] })));
  card.appendChild(selectRow('Glass thickness', [['10', '10mm, self-closing hinges'], ['8', '8mm, free hinges']], String(input.glassThickness), (v) => set({ glassThickness: v === '8' ? 8 : 10 })));
  card.appendChild(selectRow('Glass', [['clear', 'Clear'], ['low', 'Low iron'], ['acid', 'Acid etched'], ['bronze', 'Bronze'], ['grey', 'Grey'], ['refl', 'Reflective']], input.glass, (v) => set({ glass: v as GlazingOptions['glass'] })));
  if (hasTreatedFace(input.glass)) card.appendChild(selectRow('Treated side', [['E', 'Outside'], ['I', 'Inside']], input.treatedInside ? 'I' : 'E', (v) => set({ treatedInside: v === 'I' })));
  card.appendChild(selectRow('Handle', [['Q', 'Square handle, mm 980x30x30'], ['R', 'Round handle, Ø30 × 500 mm']], input.handle, (v) => set({ handle: v === 'R' ? 'R' : 'Q' })));
}

/** A single door's card: its frame in the opening (5 mm clear each side and
 *  over it), the PT10 leaf's options, its weights and passage, the spec's limits. */
function appendSingleDoorCard(body: HTMLElement, inst: ProductInstance, g: RoomGlazing): void {
  const card = document.createElement('div');
  card.className = 'glazing-card';
  const input = g.input, plan = g.plan;
  card.appendChild(sectionTitle('Single door (PT9)'));
  card.appendChild(infoRow('Material', tr('Aluminium')));
  card.appendChild(infoRow('Hinges', tr(input.hinge === 'sx' ? 'Left (seen from outside)' : 'Right (seen from outside)')));
  card.appendChild(infoRow('Wall opening', `${Math.round(input.W)} × ${Math.round(input.H)} mm`));
  card.appendChild(infoRow('Frame (outside)', `${Math.round(plan.frameW)} × ${Math.round(plan.frameH)} mm`));
  appendDoorOptions(card, g, optionSetter(inst, g));
  const kg = glazingWeights(plan, singleDoorSolids(plan));
  card.appendChild(infoRow('Door leaf + handle', `${kg.leaf.toFixed(1)} kg`));
  card.appendChild(infoRow('Total weight', `${kg.total.toFixed(1)} kg`));
  const pass = netPassage(plan, singleDoorSolids(plan, 90), SINGLE_BAND);
  card.appendChild(infoRow('Net passage', `${Math.floor(pass.w + 0.05)} × ${Math.floor(pass.h + 0.05)} mm`));
  for (const w of plan.warnings) card.appendChild(noteRow(warningText(w), 'warn', true));
  body.appendChild(card);
}

/** What a figure out of the spec says, in the UI's language. */
export function warningText(w: GlazingWarning): string {
  const range = (key: string, min: number, max: number): string => tr(key).replace('{min}', String(Math.round(min))).replace('{max}', String(Math.round(max)));
  switch (w.kind) {
    case 'cornerHinge': return tr('No corner on the hinge side next to the door: a fixed pane is needed in between.');
    case 'cornerDepth': return tr('Out of the spec: the corner needs at least {min} mm.').replace('{min}', String(Math.round(w.min)));
    case 'door': return range('Out of the spec: the door is {min}–{max} mm wide.', w.min, w.max);
    case 'height': return range('Out of the spec: the height is {min}–{max} mm.', w.min, w.max);
    case 'heightMax': return tr('Out of the spec: the wall opening is at most {max} mm high.').replace('{max}', String(w.max));
    case 'offset': return range('Out of the spec: the door’s offset from the middle is {min}–{max} mm.', w.min, w.max);
    case 'width': return range('Out of the spec: the wall opening is {min}–{max} mm wide.', w.min, w.max);
    case 'widthMax': return tr('Out of the spec: the wall opening is at most {max} mm wide.').replace('{max}', String(w.max));
  }
}
